import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Moon, BookHeart, Sparkles } from "lucide-react";
import StoryConfigurator from "../components/StoryConfigurator";
import StoryResult from "../components/StoryResult";
import FavoritesGallery from "../components/FavoritesGallery";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import SettingsDialog from "../components/SettingsDialog";
import { useSettings } from "../context/SettingsContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LS_KEY = "bedtime_favorites_v1";

export default function StoryStudio() {
  const { settings } = useSettings();
  const [story, setStory] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [tab, setTab] = useState("create");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) setFavorites(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, []);

  const persistFavorites = (next) => {
    setFavorites(next);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  const generate = async (req) => {
    const s = settings.story;
    if (!s?.apiKey) {
      toast.error("Configura tu API key en ⚙️ Ajustes para generar cuentos", {
        description: "Groq y Gemini tienen tier gratuito — sin tarjeta.",
        duration: 5000,
      });
      return;
    }

    setLoading(true);
    setLastRequest(req);
    try {
      const body = {
        ...req,
        byok: {
          provider: s.provider,
          model: s.model || null,
          api_key: s.apiKey,
        },
      };
      const { data } = await axios.post(`${API}/generate-story`, body, { timeout: 120000 });
      setStory(data);
      setTab("create");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "No se pudo generar el cuento";
      toast.error(typeof msg === "string" ? msg : "Error al generar");
    } finally {
      setLoading(false);
    }
  };

  const regenerate = () => {
    if (!lastRequest) return;
    setStory(null);
    generate(lastRequest);
  };

  const isFavorite = story && favorites.some((s) => s.id === story.id);

  const toggleFavorite = () => {
    if (!story) return;
    if (isFavorite) {
      persistFavorites(favorites.filter((s) => s.id !== story.id));
      toast.message("Quitado de favoritos");
    } else {
      persistFavorites([{ ...story }, ...favorites]);
      toast.success("¡Cuento guardado!");
    }
  };

  const openFavorite = (s) => {
    setStory(s);
    setLastRequest(null);
    setTab("create");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeFavorite = (id) => {
    persistFavorites(favorites.filter((s) => s.id !== id));
    toast.message("Cuento eliminado");
  };

  const goBack = () => {
    setStory(null);
  };

  return (
    <main className="relative min-h-screen pb-20">
      {/* Header */}
      <header
        className="px-4 sm:px-8 lg:px-12 pt-8 pb-6 flex items-center justify-between max-w-6xl mx-auto"
        data-testid="app-header"
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <Moon className="w-8 h-8 text-amber-300 drop-shadow-[0_0_12px_rgba(251,191,36,0.5)]" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-[Fredoka] text-slate-100 leading-none">
              Cuentos de Luna
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              Cuentos mágicos para dormir
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-300 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            IA narradora
          </span>
          <SettingsDialog />
        </div>
      </header>

      <section className="px-4 sm:px-8 lg:px-12 max-w-6xl mx-auto">
        {/* Hero (only when no story) */}
        {!story && (
          <div className="text-center py-10 sm:py-14" data-testid="hero">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight font-[Fredoka]">
              Un cuento{" "}
              <span className="glow-title">mágico</span> para
              <br />
              dormir esta noche
            </h2>
            <p className="text-slate-300 mt-5 max-w-xl mx-auto text-base sm:text-lg">
              Elige personajes, un lugar y un tono. La luna te tejerá una
              historia única que termina justo cuando los ojitos se cierran.
            </p>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            className="bg-slate-800/40 backdrop-blur-md border border-white/10 rounded-full p-1 mx-auto flex w-fit"
            data-testid="tabs-list"
          >
            <TabsTrigger
              value="create"
              data-testid="tab-create"
              className="rounded-full px-5 py-2 data-[state=active]:bg-amber-400 data-[state=active]:text-slate-900 text-slate-200 font-medium"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Crear
            </TabsTrigger>
            <TabsTrigger
              value="favorites"
              data-testid="tab-favorites"
              className="rounded-full px-5 py-2 data-[state=active]:bg-amber-400 data-[state=active]:text-slate-900 text-slate-200 font-medium"
            >
              <BookHeart className="w-4 h-4 mr-2" />
              Favoritos
              {favorites.length > 0 && (
                <span className="ml-2 text-xs bg-slate-900/30 rounded-full px-2 py-0.5">
                  {favorites.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="create" className="mt-10">
            {story ? (
              <StoryResult
                story={story}
                onRegenerate={regenerate}
                onBack={goBack}
                onToggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
                loading={loading}
              />
            ) : (
              <StoryConfigurator onGenerate={generate} loading={loading} />
            )}
          </TabsContent>

          <TabsContent value="favorites" className="mt-10">
            <FavoritesGallery
              favorites={favorites}
              onOpen={openFavorite}
              onRemove={removeFavorite}
            />
          </TabsContent>
        </Tabs>
      </section>

      {loading && !story && (
        <div
          className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm"
          data-testid="loading-overlay"
        >
          <div className="bg-slate-800/80 border border-amber-400/30 rounded-3xl px-8 py-6 text-center shadow-[0_0_60px_rgba(251,191,36,0.25)]">
            <Moon className="w-10 h-10 text-amber-300 mx-auto mb-3 animate-pulse" />
            <p className="text-slate-100 font-[Fredoka] text-lg">
              Tejiendo tu cuento...
            </p>
            <p className="text-slate-400 text-sm mt-1">
              La luna está eligiendo cada palabra con cariño
            </p>
          </div>
        </div>
      )}

      <footer className="text-center text-slate-500 text-xs mt-16">
        Hecho con cariño · Buenas noches 🌙
      </footer>
    </main>
  );
}
